import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import {
  isEmpty, getOr, get, last,
} from 'lodash/fp';
import EDIT from '../../Images/icons/edit.png';
import ADD from '../../Images/icons/add-small.png';
import useCurrentUser from '../../Hooks/userCurrentUser';
import {
  MANAGE_MY_COMPETITIONS,
  FETCH_EVENT_DETAIL,
  FETCH_EVENTS_USERS_BY_USERS,
  GET_USER_COALITIONS,
} from '../../GraphQL/Queries';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import useUserPermissionChecker from '../../Hooks/userUserPermissionChecker';
import ManageCompetitionItem from './ManageCompetitionItem';
import {
  AlignCenter,
} from '../../Components/Auth/Layout';
import Loader from '../../Components/Loader';
import BackLink from '../../Components/BackLink';
import Permissions from '../../Constants/PermissionConstrants';
import { EVENT_TITLE } from '../../Constants/AppConstants';

const ManageMyCompetitions = () => {
  const {
    currentUser,
  } = useCurrentUser(true);
  const checkPermission = useUserPermissionChecker(currentUser);
  const { id } = useParams();
  const [manageMyCompetitions, { data, loading: myCompsLoading }] = useLazyQuery(
    MANAGE_MY_COMPETITIONS, {
      fetchPolicy: 'no-cache',
    },
  );

  const [fetchEventDetail, { data: dataEvent, loading: eventLoading }] = useLazyQuery(FETCH_EVENT_DETAIL);
  const event = getOr({}, 'fetchEventDetail', dataEvent);

  const myCompetitions = getOr([], 'manageMyCompetitions', data);

  const [fetchEventsUsersByUsers, { data: dataEventsUser, loading: eventsUsersLoading }] = useLazyQuery(
    FETCH_EVENTS_USERS_BY_USERS,
  );

  const eventsUsers = getOr([], 'fetchEventsUsersByUser', dataEventsUser);

  const { data: coalitionsData } = useQuery(GET_USER_COALITIONS, {
    variables: { userId: get('id', currentUser) },
  });
  const myCoalitions = getOr([], 'memberSubteams', coalitionsData)
    .filter((c) => String(get('fetchEventCompetition.eventId', c)) === id);

  useHeaderComponentsMutation({
    title: 'My Competitions',
    backLink: `/event-manage/${id}`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  useEffect(() => {
    if (currentUser && id) {
      fetchEventDetail({
        variables: {
          id: parseInt(id, 10),
        },
      });
    }
  }, [currentUser, fetchEventDetail, id]);

  useEffect(() => {
    if (!isEmpty(get('fetchSchools', currentUser))) {
      manageMyCompetitions({
        variables: {
          eventId: parseInt(id, 10),
          userId: parseInt(currentUser.id, 10),
        },
      });
    }
  }, [currentUser, manageMyCompetitions, id]);

  useEffect(() => {
    if (currentUser && id) {
      fetchEventsUsersByUsers({
        variables: {
          userId: parseInt(currentUser.id, 10),
          eventId: parseInt(id, 10),
          schoolId: null,
        },
      });
    }
  }, [currentUser, fetchEventsUsersByUsers, id]);

  const submitFiles = () => {
  };

  if (myCompsLoading || eventLoading || eventsUsersLoading) {
    return <Loader />;
  }

  return (
    <>
      {myCompetitions.length === 0 && !get('teamFinalize', last(eventsUsers))
        ? (
          <>
            <div className="container px-0 pt-3 ml-0">
              <div className="row mt-3">
                <Link to={`/events/${id}/my-competitions/edit`}><img src={ADD} alt="ADD" /></Link>
                <span className="pl-4">
                  <div className="font-weight-bold">
                    Participate in your First Competition
                  </div>
                </span>
              </div>
            </div>
          </>
        )
        : (
          <div className="container px-0 pt-3 ml-0">
            <div className="row">
              <div className="col-md-8 col-8 font-weight-bold">
                 Your  Competitions
              </div>
              {checkPermission(Permissions.CAN_JOIN_COMPETITION)
                ? (
                  <>
                    {myCompetitions.length > 0 && !get('teamFinalize', last(eventsUsers))
                      ? (
                        <div className="col-md-4 col-4 text-right">
                          <Link to={`/events/${id}/my-competitions/edit`}><img src={EDIT} alt="EDIT" /></Link>
                        </div>
                      )
                      : null}
                  </>
                )
                : null}
            </div>
          </div>
        )}

      {myCompetitions.map((user, index) => (
        <ManageCompetitionItem
          key={user.id}
          competition={user}
          index={index}
          onClick={() => submitFiles()}
          backgroundColor={(user.status === 'joined') ? '#389446' : '#3E6796'}
        />
      ))}

      {myCoalitions.length > 0 && (
        <div className="container px-0 pt-4 ml-0">
          <div className="row">
            <div className="col-12 font-weight-bold pb-2">
              Your Coalitions
            </div>
          </div>
          {myCoalitions.map((coalition) => (
            <div key={coalition.id} className="border rounded mb-3">
              <div
                className="px-3 py-2 font-weight-bold text-white"
                style={{ background: '#3E6796' }}
              >
                <div>
                  {get('fetchEventCompetition.title', coalition)}
                </div>
                <div className="small" style={{ opacity: 0.85 }}>
                  {'Coalition: '}
                  {coalition.code}
                </div>
              </div>
              {(coalition.fetchCoalitionsSchools || []).map((cs) => (
                <div key={cs.id} className="px-3 py-2 border-top">
                  <div className="font-weight-bold small pb-1">
                    {get('fetchSchool.name', cs)}
                  </div>
                  {(cs.fetchCoalitionMembers || []).map((cm) => (
                    <div key={cm.id} className="d-flex align-items-center py-1">
                      {cm.user.profileImageUrl ? (
                        <img
                          src={cm.user.profileImageUrl}
                          alt=""
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: 8,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: '#ccc',
                            marginRight: 8,
                          }}
                        />
                      )}
                      <span className="small">
                        <span style={{ color: '#888' }}>
                          {cm.user.code}
                        </span>
                        {' '}
                        {cm.user.firstname}
                        {' '}
                        {cm.user.lastname}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <AlignCenter>
        <BackLink to={`/event-manage/${id}`}>
       Manage Event
        </BackLink>
      </AlignCenter>
    </>
  );
};

export default ManageMyCompetitions;
